import { ChangeEventHandler, FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Col } from "reactstrap";
import { Button, FileInfo, HelpHint } from "src/Components";
import { IFormProps } from ".";
import { CollectionViewModel } from "../Models";
import BaseViewModel from "../Models/BaseViewModel";
import FormViewModel from "../Models/FormViewModel";
import { ReactComponent as DeleteIcon } from "src/Themes/assets/Delete.svg";
import { ReactComponent as ScanIcon } from "../../Scan/assets/skan.svg";
import st from "../DynamicForm.module.scss";
import { ScanFileType, useScan } from "src/Modules/Scan";
import useModal from "src/Hooks/useModal";
import CollectionItem from "./CollectionItem";
import { DynamicFormPartType } from "../Types";

interface ICollectionFormControlProps extends IFormProps {
	model: CollectionViewModel;
	readOnly?: boolean;
};

const CollectionFormControl: FC<ICollectionFormControlProps> = ( props ) => {

	const { model, readOnly = false } = props;

	const [ ,setNeedUpdate ] = useState(false);
	const forceUpdate = () => setNeedUpdate( pr => !pr );

	const isReadOnly = readOnly || !model.ControlEnabling;
	const hideAddButton = Number(model.MaxItemsCount) === 1;
	const { showError } = useModal();
	const scanAvailable = !!BaseViewModel.scanSettings && !!BaseViewModel.scanSettings.scanModuleWorkUrl && BaseViewModel.scanSettings.scanModuleWorkUrl.length > 7;
	const { showModal, setShowModal, renderModal } = useScan( scanAvailable, setBlobFunctionHandler );

	/** Вызывается модулем сканировании при получении файла */
	async function setBlobFunctionHandler ( response: ScanFileType, count: number ) {
		const file = new File( [ await response.result.arrayBuffer() ], response.filename, { type: response.result.type } );
		if ( !!file ) {
			attachFileToNewItem( file );
		}
	};

    const [ files, setFiles ] = useState<File[]>([]);
    const [ currentIndex, setCurrentIndex ] = useState<number | null>(null);

	const getDisplaySize = (fileName?: string, fallback?: number) => {
		const name = fileName || "";
		const fromMap = BaseViewModel.attachmentsWithSizes?.[name];
		const mapped = (typeof fromMap === "number" && fromMap > 0) ? fromMap : undefined;
		const raw = (fallback && fallback > 0) ? fallback : undefined;
		return raw ?? mapped;
	};

    // Синхронизируем список файлов из модели (для закрытых/переоткрытых задач)
    useEffect( () => {
        const result: File[] = [];
        const traverse = ( vm: any ) => {
            if ( !vm ) return;
            if ( vm.DescriptorKey === "SimpleAttachmentViewModel" ) {
                const val = vm?.Value;
                if ( val && typeof val === "object" && typeof (val as any)?.length === "number" && (val as any)?.length ) {
                    try {
                        Array.from(val as FileList).forEach( (f: File) => f?.name && result.push(f) );
                    } catch {}
                }
                else if ( typeof val === "string" && !!val ) {
                    result.push( new File( [], val ) );
                }
                else if ( typeof vm.getCleanValue === "function" ) {
                    const name = vm.getCleanValue();
                    if ( name ) result.push( new File( [], name ) );
                }
            }
            if ( Array.isArray(vm?.FormElements) ) vm.FormElements.forEach(traverse);
        };
        if ( Array.isArray(model?.CollectionItems) ) {
            model.CollectionItems.forEach( traverse );
        }
        const uniq = new Map<string, File>();
        result.forEach( f => {
            const disp = getDisplaySize(f?.name, f?.size || 0) || 0;
            const key = (f?.name || "") + "::" + disp;
            if ( !uniq.has(key) ) uniq.set(key, f);
        });
        setFiles(Array.from(uniq.values()));
    }, [ model, isReadOnly ] );

    const inputRef = useRef<HTMLInputElement>(null);

	const isAttachmentOnlyCollection = (() => {
		const analyzeDescriptor = (descriptor?: DynamicFormPartType) => {
			const state = { hasAttachment: false, hasOther: false };
			const walk = (desc?: DynamicFormPartType) => {
				if (!desc) return;
				if (desc.DescriptorKey === "SimpleAttachmentViewModel") {
					state.hasAttachment = true;
					return;
				}
				const hasChildren = Array.isArray(desc.FormElements) || !!desc.CollectionElementDescriptor;
				if (!hasChildren) {
					state.hasOther = true;
					return;
				}
				if (Array.isArray(desc.FormElements)) {
					desc.FormElements.forEach(walk);
				}
				if (desc.CollectionElementDescriptor) {
					walk(desc.CollectionElementDescriptor);
				}
			};
			walk(descriptor);
			return state;
		};
		const result = analyzeDescriptor(model?.elementDescriptor?.CollectionElementDescriptor);
		return result.hasAttachment && !result.hasOther;
	})();

    const toUniqueFiles = (items: File[]): File[] => {
        const seen = new Set<string>();
        const res: File[] = [];
        for ( const f of items ) {
            const disp = getDisplaySize(f?.name, f?.size || 0) || 0;
            const key = (f?.name || "") + "::" + disp;
            if (!seen.has(key)) { seen.add(key); res.push(f); }
        }
        return res;
    };

	const makeKey = (f: File) => {
		const disp = getDisplaySize(f?.name, f?.size || 0) || 0;
		return ( (f?.name || "") + "::" + disp );
	};

	const attachFileToNewItem = ( selectedFile: File ) => {
		try {
			const findAttachment = ( vm: any ): any => {
				if ( vm?.DescriptorKey === "SimpleAttachmentViewModel" ) return vm;
				if ( !!vm?.FormElements?.length ) {
					for ( let i = 0; i < vm.FormElements.length; i++ ) {
						const found = findAttachment( vm.FormElements[i] );
						if ( !!found ) return found;
					}
				}
				return null;
			};
			// всегда создаём новый элемент коллекции под файл
			model.addItem();
			const idx = Math.max(0, (model.CollectionItems?.length || 1) - 1);
			const newItem = model.CollectionItems[idx];
			const attachmentModel = findAttachment( newItem );
			if ( attachmentModel ) {
				const dt = new DataTransfer();
				dt.items.add( selectedFile );
				props.setFieldValue( attachmentModel.FullPath, dt.files );
				attachmentModel.setValue( dt.files );
			}
			setCurrentIndex(idx);
			setFiles(prev => toUniqueFiles([ ...(prev || []), selectedFile ]));
			forceUpdate();
		}
		catch (e) { console.warn(e); }
	};

	const clearAttachmentInItem = ( index: number ) => {
		try {
			type AttachmentModel = {
				DescriptorKey: "SimpleAttachmentViewModel";
				FullPath: string;
				setValue?: ( value: FileList | string | null ) => void;
			};
			const isAttachmentModel = (v: unknown): v is AttachmentModel => {
				if ( !v || typeof v !== "object" ) return false;
				const o = v as Record<string, unknown>;
				return o["DescriptorKey"] === "SimpleAttachmentViewModel" && typeof o["FullPath"] === "string";
			};
			const isNodeWithChildren = (v: unknown): v is { FormElements: unknown[] } => {
				if ( !v || typeof v !== "object" ) return false;
				const o = v as Record<string, unknown>;
				return Array.isArray(o["FormElements"]);
			};
			const findAttachment = ( vm: unknown ): AttachmentModel | null => {
				if ( isAttachmentModel(vm) ) return vm;
				if ( isNodeWithChildren(vm) ) {
					for ( const child of vm.FormElements ) {
						const found = findAttachment( child );
						if ( found ) return found;
					}
				}
				return null;
			};
			const item = model?.CollectionItems?.[index];
			if ( !item ) return;
			const attachmentModel = findAttachment( item );
			if ( !attachmentModel ) return;
			props.setFieldValue( attachmentModel.FullPath, null );
			attachmentModel.setValue?.( null );
			forceUpdate();
		} catch (e) { console.warn(e); }
	};

    /** Сохранение выбранных файлов ( метаданных ) */
	const onChangeValueHandler: ChangeEventHandler<HTMLInputElement> = ( e ) => {
		const list = !!e.target?.files && e.target.files.length ? Array.from(e.target.files) : null;
		if ( !!list && list.length ) {
			list.forEach( f => {
				// временно допускаем 0 Кб, пока бэкенд не начнёт отдавать размер
				attachFileToNewItem( f );
			});
		}
	};

    /** Эмулирует клик по инпуту ( открывает окно выбора файла ) */
	const onChooseFile = () => {
		if ( !!inputRef.current ) {
			inputRef.current.click();
		}
	};

	/** Добавление нового элемента коллекции: файл или форма */
	const handleAddClick = () => {
		if ( isAttachmentOnlyCollection ) {
			onChooseFile();
			return;
		}
		model.addItem();
		forceUpdate();
	};

    const onRemoveUploadedFile = () => {
        setFiles([]);
		if ( inputRef.current ) {
			inputRef.current.value= '';
		}
        if ( currentIndex !== null ) {
			try { model.remove(currentIndex); } catch(e) { console.warn(e); }
            setCurrentIndex(null);
			forceUpdate();
        }
	};

	const onRemoveUploadedFileByKey = ( fileKey: string ) => {
		setFiles(prev => prev.filter( f => makeKey(f) !== fileKey ));
		if ( inputRef.current ) {
			inputRef.current.value= '';
		}
		// найти индекс элемента коллекции по имени/размеру
		const [name, sizeStr] = fileKey.split('::');
		const sizeNum = parseInt(sizeStr || '0', 10) || 0;
		const findAttachment = ( vm: any ): any => {
			if ( vm?.DescriptorKey === "SimpleAttachmentViewModel" ) return vm;
			if ( !!vm?.FormElements?.length ) {
				for ( let i = 0; i < vm.FormElements.length; i++ ) {
					const found = findAttachment( vm.FormElements[i] );
					if ( !!found ) return found;
				}
			}
			return null;
		};
		let idxToRemove: number | null = null;
		if ( Array.isArray(model?.CollectionItems) ) {
			for ( let i = 0; i < model.CollectionItems.length; i++ ) {
				const attachmentModel = findAttachment( model.CollectionItems[i] );
				if ( !attachmentModel ) continue;
				const val = attachmentModel?.Value;
				if ( val && typeof val === 'object' && typeof (val as any).length === 'number' && (val as any).length ) {
					try {
						const fl = Array.from(val as FileList);
						const f0 = fl[0];
						const disp = getDisplaySize(f0?.name, f0?.size || 0) || 0;
						if ( f0 && f0.name === name && disp === sizeNum ) { idxToRemove = i; break; }
					} catch {}
				} else {
					let storedName: string | null = null;
					if ( typeof val === 'string' && !!val ) storedName = val;
					else if ( typeof attachmentModel.getCleanValue === 'function' ) {
						try { const n = attachmentModel.getCleanValue(); if ( n ) storedName = n; } catch {}
					}
					if ( storedName && storedName === name ) { idxToRemove = i; break; }
				}
			}
		}
		if ( idxToRemove !== null ) {
			clearAttachmentInItem(idxToRemove);
		}
	};

	const renderFileTb = ( fileKey: string ) => (
		<>
			<Button outline variant="red" size="min" onClick={() => onRemoveUploadedFileByKey(fileKey)}>
				<DeleteIcon />
			</Button>
		</>
	);

	return (
		<Col md={model.Width ?? 12} className={st.collectionBlock} hidden={!model.ControlVisibility}>
			<div className={st.collectionBlock_Header}>
				<h3 className={classNames("headline-small",st.collectionBlock_Header_Title)}>{ model.Caption }</h3>
				{ !!model.Tooltip && <HelpHint title={ model.Tooltip } /> }
				{
					!isReadOnly && !hideAddButton && (
						<div className={st.collectionBlock_Header_Buttons}>
						{ (scanAvailable && isAttachmentOnlyCollection) && (
								<Button onClick={() => setShowModal(true)}>
									<ScanIcon width={24} height={24} />
									Сканировать
								</Button>
							)}
						<Button variant="blue" iconClass="icon-plus" disable={!model.checkShowAddItemsButton()} onClick={handleAddClick}>
								{ model.ButtonCaption }
							</Button>
						</div>
					)
				}
			</div>
			{ files.map( f => {
				const fkey = makeKey(f);
				const dispSize = getDisplaySize(f?.name, f?.size || 0);
				return (
					<FileInfo key={fkey} title={f.name} size={dispSize} innerToolbar={!isReadOnly} renderToolbar={!isReadOnly ? () => renderFileTb(fkey) : undefined} />
				);
			}) }
		{ (!isAttachmentOnlyCollection && Array.isArray(model?.CollectionItems)) && model.CollectionItems.map( (item: FormViewModel, index: number) => (
			<CollectionItem
				key={item?.FullPath ?? ("collection_item_" + index)}
					model={item}
					index={index}
					readOnly={isReadOnly}
					setFieldValue={props.setFieldValue}
					onRemove={(i) => { 
						try { 
							model.remove(i); 
						} catch(e) { 
							console.warn(e); 
						}
						forceUpdate();
					}}
				/>
			)) }
			{ isAttachmentOnlyCollection && (
				<input
					ref={inputRef}
					hidden
					type='file'
					multiple
					onChange={onChangeValueHandler}
				/>
			)}
			{ showModal && createPortal( renderModal() , document.body ) }
		</Col>
	);
};

export default CollectionFormControl;
